import React, { Fragment, useContext, useState } from 'react';
import { Divider, Link, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { SelectedServiceType } from 'compositions/Wizard/Confirmation/WzardPartsAndServices';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetPrices } from 'hooks/useGetPrices';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { AnalyticsAction, AnalyticsCategory } from 'hooks/useGoogleAnalytics/types';
import { Part } from 'types/UFOTypes';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import { getTotalPartTAX } from 'utils/taxes';
import ServiceInformation from './ServiceInformation';

interface InstallationsListProps {
  status: string;
  printMode?: boolean;
  setSelected?: (i: Part) => void;
  isEditing?: boolean;
  disabled?: boolean;
  installations?: SelectedServiceType[];
  setPart?: (parts: Part) => void;
  setQty?: (qty: number, service: SelectedServiceType) => void;
  commonPricesParams: CommonPricesParamsType;
  smallSize?: boolean;
  shoppingCartMode?: boolean;
  maxQty: number;
}

export default function InstallationsList(props: InstallationsListProps) {
  const {
    status,
    printMode = false,
    setSelected,
    isEditing,
    disabled,
    installations,
    setPart,
    commonPricesParams,
    smallSize,
    setQty,
    shoppingCartMode,
    maxQty,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const [expanded, setExpand] = useState(false);
  const isDealer = useCurrentLocationIsDealer();
  const { someEmptyPrice } = useContext(OrderDetailContext);
  const googleAnalyticsUtils = useGoogleAnalytics();

  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    parts:
      installations
        ?.filter((i) => !i.noAddedInstallation)

        .map((i) => ({ is_pql: false, mspn: i.part_id, qty: i.quantity })) || [],
  });

  if (!installations || installations.length === 0) return null;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const total = { total_invoice_price: 0, totalFinalInvoicePrice: 0, totalTaxes: 0 };
  if (!pricesResponse.data?.parts.some((p) => !p.invoice_price || Number(p.invoice_price) === 0)) {
    pricesResponse.data?.parts.forEach((fp) => {
      total.total_invoice_price += Number(fp.invoice_price);
      total.totalFinalInvoicePrice += Number(fp.final_invoice_price);
      total.totalTaxes += getTotalPartTAX(fp);
    });
  }

  const installationsSelected = installations.filter((i) => !i.noAddedInstallation).length;

  return (
    <>
      {(expanded || printMode) && (
        <>
          {/* TODO: improve typing */}
          {installations.map((service: any) => (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-template-expressions
            <Fragment key={`installationList${getMspn(service) ?? ''}${service.related_to ?? ''}`}>
              {((isEditing && !disabled) || !service.unselectedInstallation) && (
                <>
                  <Divider
                    style={{
                      marginTop: 16,
                      marginBottom: 16,
                      width: '92%',
                      marginLeft: '8%',
                    }}
                  />
                  <ServiceInformation
                    isInstallation
                    maxQty={maxQty}
                    disabled={disabled}
                    qty={service.quantity}
                    setQty={
                      setQty
                        ? (q) => {
                            googleAnalyticsUtils.event({
                              category:
                                status === 'wizard'
                                  ? AnalyticsCategory.ORDER_CREATION
                                  : AnalyticsCategory.ORDER_EDITION,
                              action: AnalyticsAction.INSTALLATION_EDITED,
                              isDealer: !!isDealer,
                            });
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                            setQty(q, service);
                          }
                        : undefined
                    }
                    status={status}
                    selected={
                      shoppingCartMode
                        ? !service.removed
                        : !service.unselectedInstallation && service.selected !== false
                    }
                    setSelected={() => {
                      googleAnalyticsUtils.event({
                        category:
                          status === 'wizard' ? AnalyticsCategory.ORDER_CREATION : AnalyticsCategory.ORDER_EDITION,
                        action: AnalyticsAction.INSTALLATION_EDITED,
                        isDealer: !!isDealer,
                      });
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                      if (setSelected) setSelected(service);
                    }}
                    service={service}
                    printMode={printMode}
                    isEditing={isEditing}
                    orderPrice={service.final_invoice_price !== undefined ? service : undefined}
                    commonPricesParams={commonPricesParams}
                    is_pql={service.pql_pricing || false}
                    setPart={setPart}
                    smallSize={smallSize}
                    mobileView={mobileView}
                    shoppingCartMode={shoppingCartMode}
                  />
                </>
              )}
            </Fragment>
          ))}
          {!printMode && (
            <div style={{ textAlign: 'right', width: '100%' }}>
              {/* TODO: check this or note why to use Link over Button */}
              <Link onClick={() => setExpand(false)} style={{ cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>
                {t('Collapse installations detail')}
              </Link>
            </div>
          )}
        </>
      )}
      {!printMode && !expanded && (installationsSelected > 0 || isEditing) && (
        <div style={{ width: '100%', textAlign: 'right' }}>
          {!isDealer && !someEmptyPrice && total.total_invoice_price > 0 && (
            <>
              <Typography style={{ fontSize: 10 }}>
                {t('Installations price: ${{totalInvoicePrice}}', {
                  totalInvoicePrice: total.total_invoice_price.toFixed(2),
                })}
              </Typography>
              <Typography style={{ fontSize: 10 }}>
                {t('Installation taxes: ${{totalTax}}', {
                  totalTax: total.totalTaxes.toFixed(2),
                })}
              </Typography>
              <Typography style={{ fontSize: 10, fontWeight: 'bold' }}>
                {t('Total installations : ${{totalTax}}', {
                  totalTax: total.totalFinalInvoicePrice.toFixed(2),
                })}
              </Typography>
            </>
          )}
          {/* TODO: check this or note why to use Link over Button */}
          <Link onClick={() => setExpand(true)} style={{ cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>
            {installationsSelected > 0 && t('Expand installations detail')}
            {installationsSelected === 0 && t('+Add installations')}
          </Link>
        </div>
      )}
    </>
  );
}
